<template>
  <div class="partner-link-panel">
    <template v-if="!storeCd">
      <el-alert :title="$t(text.noStore)" type="info" :closable="false" />
    </template>

    <template v-else>
      <h4>{{ $t(text.panelTitle) }}</h4>

      <!-- 조회 실패는 「채널 없음」이 아니다 — 실패라고 말하고 다시 시도할 자리를 준다 -->
      <el-alert v-if="loadFailed" :title="$t(text.loadFailed)" type="error" :closable="false" show-icon>
        <el-button size="mini" type="text" @click="load">{{ $t(text.retryAction) }}</el-button>
      </el-alert>

      <template v-else>
        <!--
          **콜팝 참여는 채널 표의 행이 아니다** — 접수·통지·요청 ID 가 없는 매장 속성이라 열이 겹치지 않는다
          (`participation` 은 서버 응답에서도 `links[]` 밖이다). 같은 표에 끼워 넣으면 「통지일」·「판정」 같은
          열을 채널마다 다른 뜻으로 읽게 되므로, 자기 열을 가진 한 행으로 채널 표 **위**에 둔다.
        -->
        <div v-if="participationRows.length > 0" class="participation">
          <el-table :data="participationRows" size="small" border>
            <el-table-column :label="$t(text.participationTitle)" width="130">
              <template slot-scope="scope">{{ scope.row.longName }}</template>
            </el-table-column>

            <!--
              `slot-scope` 를 붙이는 이유: 없으면 element-ui 가 기본 슬롯을 **숨은 컬럼 컨테이너**에도 한 번
              그려 chip 인스턴스가 하나 더 생기고, 참여 행이 여럿이 되는 날 모든 행이 같은 정적 vnode 를 쓴다.
            -->
            <el-table-column :label="$t(text.stateColumn)" width="190">
              <template slot-scope="scope">
                <PartnerLinkChip :view="view" :partner="scope.row.chipKey" :clickable="false" />
              </template>
            </el-table-column>

            <el-table-column :label="$t(text.joinedAtColumn)" width="170">
              <template slot-scope="scope">{{ scope.row.joinedAtText }}</template>
            </el-table-column>

            <el-table-column :label="$t(text.endedAtColumn)" width="170">
              <template slot-scope="scope">{{ scope.row.endedAtText }}</template>
            </el-table-column>

            <el-table-column :label="$t(text.sourceColumn)" min-width="150">
              <template slot-scope="scope">
                {{ scope.row.sourceText || '-' }}
                <span v-if="scope.row.actorText" class="muted">({{ scope.row.actorText }})</span>
              </template>
            </el-table-column>

            <!--
              **참여 조건**(B7-b §3-10) — 서버가 이 뷰에 실어 주므로 별도 조회가 없다. 조건 축이 없는
              응답(참여 조건 회차 배포 전)에서는 칸 자체를 그리지 않는다: 「없음」을 「미달」로 접으면
              화면이 서버가 모르는 사실을 단정한다.
            -->
            <el-table-column :label="$t(callpopText.eligibilityColumn)" min-width="170">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.eligibility" size="mini" :type="scope.row.eligibility.tagType">{{
                  scope.row.eligibility.text
                }}</el-tag>
                <span v-else>-</span>
              </template>
            </el-table-column>

            <el-table-column :label="$t('관리')" width="140">
              <template slot-scope="scope">
                <!--
                  권한이 없다고 버튼을 **숨기지 않는다** — 사라진 버튼은 「이 화면에 그 기능이 없다」로 읽힌다.
                  끈 채로 이유를 툴팁에 남긴다(역할 문자열로 미리 판정하지 않고 서버의 403 을 따른다).
                  조건 미달도 같은 규칙이다 — 잠그고 **왜인지** 말한다(서버 400 도 같은 사유를 답한다).
                -->
                <el-tooltip placement="top" :disabled="!joinBlockedTip" :content="$t(joinBlockedTip || '')">
                  <span>
                    <el-button
                      v-if="scope.row.chip.actions.join"
                      size="mini"
                      type="primary"
                      plain
                      :disabled="busy || participationDenied || !!joinLock"
                      @click="confirmJoin()"
                      >{{ participationDenied ? $t(text.participationDenied) : $t(text.joinAction) }}</el-button
                    >
                    <el-button
                      v-if="scope.row.chip.actions.end"
                      size="mini"
                      type="danger"
                      plain
                      :disabled="busy || participationDenied"
                      @click="confirmEnd()"
                      >{{ participationDenied ? $t(text.participationDenied) : $t(text.endAction) }}</el-button
                    >
                  </span>
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>

          <!--
            축 설명은 상태와 무관한 사실이라 고정이고, 두 번째 문장은 **그 매장의 상태**가 정한다 —
            상태→문구 매핑은 `utils/partnerLink` 의 chip 표 한 곳이 소유한다(해지된 매장 아래에
            「전달됩니다」가 남으면 표와 각주가 서로 다른 말을 한다).
          -->
          <p class="participation__note">
            {{ $t(text.participationAxisNote) }} {{ $t(participationRows[0].chip.note) }}
          </p>

          <!--
            **관리점 매핑**(B7-b §3-10 `agencyMapping`) — 같은 뷰가 실어 주는 사실이라 조회가 늘지 않는다.
            매핑이 없으면 「매핑 없음」이고, 관리점이 없으면 운영사 직할(총괄사면 총괄사 직할)이다.
            편성·이관은 이 화면이 하지 않는다 — 콜팝 운영 화면의 몫이다.
          -->
          <p class="participation__mapping">
            <span class="participation__mapping-title">{{ $t(callpopText.agencyLabel) }}</span>
            <span>{{ agencyMappingText }}</span>
          </p>
        </div>

        <el-table :data="rows" size="small" border v-loading="loading" :empty-text="$t(text.noLink)">
          <el-table-column :label="$t(text.channelColumn)" width="130">
            <template slot-scope="scope">{{ scope.row.longName }}</template>
          </el-table-column>

          <el-table-column :label="$t(text.stateColumn)" width="190">
            <template slot-scope="scope">
              <PartnerLinkChip :view="view" :partner="scope.row.partner" :clickable="false" />
            </template>
          </el-table-column>

          <el-table-column :label="$t(text.judgeColumn)" width="110">
            <template slot-scope="scope">
              {{ scope.row.subscribedText }}
              <span v-if="scope.row.sourceText" class="muted">({{ scope.row.sourceText }})</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t(text.receiptColumn)" width="180">
            <template slot-scope="scope">{{ scope.row.receiptText }}</template>
          </el-table-column>

          <el-table-column :label="$t(text.checkedAtColumn)" width="150">
            <template slot-scope="scope">{{ scope.row.link.checkedAt || '-' }}</template>
          </el-table-column>

          <el-table-column :label="$t(text.eventDateColumn)" width="110">
            <template slot-scope="scope">{{ scope.row.link.eventDate || '-' }}</template>
          </el-table-column>

          <!--
          연동일·해지일은 **새 열**이다 — 「통지일」을 채널마다 다른 뜻으로 쓰지 않는다(그 열은 KT 배치 통지의
          날짜다). 그 축을 쓰지 않는 채널은 `'-'` 로 남는다.
        -->
          <el-table-column :label="$t(text.linkedAtColumn)" width="200">
            <template slot-scope="scope">{{ scope.row.linkedAtText }}</template>
          </el-table-column>

          <el-table-column :label="$t(text.requestIdColumn)" min-width="200">
            <template slot-scope="scope">
              <template v-if="scope.row.link.lastRequestId">
                <span class="request-id">{{ scope.row.link.lastRequestId }}</span>
                <el-button size="mini" type="text" icon="el-icon-copy-document" @click="copyRequestId(scope.row)">{{
                  $t(text.copyAction)
                }}</el-button>
              </template>
              <template v-else>-</template>
            </template>
          </el-table-column>

          <el-table-column :label="$t(text.failReasonColumn)" width="140">
            <template slot-scope="scope">{{ scope.row.failText }}</template>
          </el-table-column>

          <!--
          표가 가로로 넘친다(열 폭 합이 1600px 대) — 「연동하기」·「연동 해지」가 스크롤 뒤에 숨으면 이 화면의
          할 일 자체가 보이지 않는다. 오른쪽에 고정해 어느 폭에서도 첫 화면에 남긴다 (compose 실측 1366px).
        -->
          <el-table-column :label="$t('관리')" width="260" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.chip.actions.check"
                size="mini"
                icon="el-icon-search"
                :disabled="busy"
                @click="check(scope.row)"
                >{{ $t(text.checkAction) }}</el-button
              >
              <el-button
                v-if="scope.row.chip.actions.apply"
                size="mini"
                type="primary"
                plain
                :disabled="busy"
                @click="confirmApply(scope.row)"
                >{{ $t(text.applyAction) }}</el-button
              >
              <el-button
                v-if="scope.row.chip.actions.retry"
                size="mini"
                type="danger"
                plain
                :disabled="busy"
                @click="confirmApply(scope.row)"
                >{{ $t(text.retryAction) }}</el-button
              >
              <!-- 연동은 이 화면이 하지 않는다 — 네아로 로그인이 필요해 위저드를 새 창으로 연다(계획 §2.1 C) -->
              <el-button
                v-if="scope.row.chip.actions.link"
                size="mini"
                type="primary"
                plain
                icon="el-icon-link"
                @click="openWizard(scope.row)"
                >{{ $t(text.linkAction) }}</el-button
              >
              <el-button
                v-if="scope.row.chip.actions.unlink"
                size="mini"
                type="danger"
                plain
                :disabled="busy"
                @click="confirmUnlink(scope.row)"
                >{{ $t(text.unlinkAction) }}</el-button
              >
              <el-button v-if="scope.row.chip.actions.info" size="mini" type="text" @click="showInfo(scope.row)">{{
                $t(text.infoAction)
              }}</el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>

      <!--
        막힌 사유는 **채널마다 다른 사실**이다 — 첫 행의 사유로 표 전체를 설명하지 않는다.
        채널명을 여기서 앞에 붙이지 않는다: 문장이 스스로 채널을 말한다(`PARTNER_LINK_TEXT` 의 차단 문구 규칙).
      -->
      <p v-for="blocked in blockedLines" :key="blocked.key" class="blocked">{{ $t(blocked.text) }}</p>

      <!-- 가입 플레이스 목록(재정리 §4) — KT 축이다. 표시만 하고 편성·탈퇴는 플레이스 관리에서 -->
      <div v-if="view && hasMytown" class="places">
        <span class="places__title">{{ $t(text.placesTitle) }}</span>
        <template v-if="places.length > 0">
          <el-tag
            v-for="place in places"
            :key="place.id"
            size="small"
            class="places__tag"
            :type="place.conditionExemptYn === 'Y' ? 'warning' : ''">
            {{ place.name }}
          </el-tag>
          <span class="places__note">{{ $t(text.placesNote) }}</span>
        </template>
        <span v-else class="places__note">{{ $t(text.noPlaces) }}</span>
      </div>

      <!-- 화면에 반드시 남길 문구 (KT §1.4 · 네이버 §5.2) — 순서·내용은 채널 규칙이 소유한다 -->
      <div v-for="group in noteGroups" :key="group.key" class="notes-group">
        <p v-if="noteGroups.length > 1" class="notes-group__title">{{ group.name }}</p>
        <ul class="notes">
          <li v-for="(line, index) in group.lines" :key="index">{{ $t(line) }}</li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import PartnerLinkChip from '@/components/store/PartnerLinkChip.vue'
import { httpStatusOf } from '@/plugins/http/policy'
import { eligibilityChip, joinLockReason, storeOwnerView } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { copyText } from '@/utils/clipboard'
import { latestGuard } from '@/utils/latestGuard'
import {
  PARTICIPATION_ACTION_PATH,
  PARTICIPATION_CHIP_KEY,
  PARTICIPATION_REASON_MAX,
  PARTICIPATION_WRITE_CONFIG,
  PARTICIPATION_WRITE_RESULT,
  PARTNER,
  PARTNER_ACTION_PATH,
  PARTNER_LINK_API,
  PARTNER_LINK_TEXT,
  applyConfirmMessage,
  channelNotes,
  endConfirmMessage,
  joinConfirmMessage,
  naverPlaceWizardPath,
  participationChip,
  participationEndBody,
  participationOf,
  participationSourceText,
  participationWriteResult,
  partnerActionUrl,
  partnerCheckNotice,
  partnerDisplayName,
  partnerInfoMessage,
  partnerJudgeText,
  partnerLinkChip,
  partnerLinkedAtText,
  partnerLongName,
  unlinkConfirmMessage,
} from '@/utils/partnerLink'

/**
 * 매장 하나의 **외부 채널 연동** 패널 (KT 계획 §1.1 A·A′ · §4.1 · 네이버 계획 §2.1 A).
 *
 * 상태·문구·버튼 가시성은 전부 `utils/partnerLink` 가 만든다 — 여기에는 `state → 한글` 매핑이 없다(§4.2).
 * 행은 서버가 내린 `links[]` 를 그대로 따르므로 채널이 늘면 표가 저절로 는다.
 *
 * 호출은 `this.$axios`(modern)이고 실패 메시지는 인터셉터가 서버의 한글 문장을 그대로 띄운다. KT 의 신청 실패는
 * 예외가 아니라 `state=REQUEST_FAILED` 로 오므로(§1.3 3번) 화면은 그 상태를 그리고 「다시 시도」만 준다.
 */
export default {
  name: 'PartnerLinkPanel',

  components: { PartnerLinkChip },

  props: {
    storeCd: { type: Number, default: null },
    /** 부모가 올리면 같은 매장을 다시 읽는다 — 매장 저장 뒤(사업자번호·전화·매장명이 바뀐 뒤) 뷰가 낡지 않게 */
    reloadSeq: { type: Number, default: 0 },
  },

  data() {
    return {
      loading: false,
      busy: false,
      /** 마지막 조회가 실패했는가 — `noLink`(정상적으로 채널 없음)와 구분해 그린다 */
      loadFailed: false,
      /**
       * 늦게 도착한 이전 요청(다른 매장·저장 전 조회)의 응답이 최신 뷰를 덮지 못하게 — 관용구는
       * `utils/latestGuard` 가 소유한다(네이버 위저드도 같은 것을 쓴다).
       */
      guard: latestGuard(),
      view: null,
      text: PARTNER_LINK_TEXT,
      /** 콜팝 조직 축(관리점·참여 조건)의 문구는 그 축이 소유한다 — 두 파일이 같은 낱말을 적지 않는다 */
      callpopText: CALLPOP_TEXT,
      /**
       * 참여 쓰기가 403 을 받았는가 — 매장마다의 사실이라 `storeCd` 가 바뀌면 지운다.
       * 미리 역할로 판정하지 않는다: 이 계정이 **그 매장의** 참여를 바꿀 수 있는지는 서버만 안다(매장 스코프).
       */
      participationDenied: false,
    }
  },

  computed: {
    rows() {
      const links = this.view && Array.isArray(this.view.links) ? this.view.links : []
      const bizNoStatus = this.view ? this.view.bizNoStatus : null
      return links.map((link) => ({
        link,
        partner: link.partner,
        name: partnerDisplayName(link.partner),
        longName: partnerLongName(link.partner),
        chip: partnerLinkChip(link, bizNoStatus),
        // 「판정」 칸 — 그 칸이 없는 채널(네이버)은 `-` 다
        ...partnerJudgeText(link),
        receiptText: this.receiptTextOf(link),
        linkedAtText: partnerLinkedAtText(link),
        failText: link.failReason || '-',
      }))
    },
    /**
     * 참여 행 — 서버가 그 축을 내릴 때만 **한 행**이다(`participation` 이 없으면 빈 배열 = 아무것도 그리지 않는다).
     * 한 행짜리 표인 이유는 템플릿 주석 참고. `chip` 은 요약줄 chip 과 같은 함수가 만든다.
     */
    participationRows() {
      const participation = participationOf(this.view)
      if (!participation) return []
      return [
        {
          participation,
          // 행이 자기 chip 코드를 갖는다 — 상태 열이 행에서 값을 꺼내므로 행이 여럿이 되어도 축이 안 갈린다
          chipKey: PARTICIPATION_CHIP_KEY,
          longName: partnerLongName(PARTICIPATION_CHIP_KEY),
          chip: participationChip(participation),
          joinedAtText: participation.joinedAt || '-',
          endedAtText: participation.endedAt || '-',
          sourceText: participationSourceText(participation.source),
          actorText: participation.actor || '',
          eligibility: eligibilityChip(this.view && this.view.eligibility),
        },
      ]
    },
    /** 참여 등록을 막는 이유 — 조건 축이 없는 응답에서는 `null`(잠그지 않는다) */
    joinLock() {
      return joinLockReason(this.view && this.view.eligibility)
    },
    /** 툴팁 한 줄 — 권한이 먼저고 그다음이 조건이다(둘 다면 권한이 더 근본적인 이유다) */
    joinBlockedTip() {
      if (this.participationDenied) return this.text.participationDeniedTip

      return this.joinLock
    },
    /** 「관리점」 행 — 매핑이 없으면 「매핑 없음」, 관리점이 없으면 직할이다 */
    agencyMappingText() {
      const mapping = (this.view && this.view.agencyMapping) || null
      if (!mapping) return this.callpopText.mappingNone

      const owner = storeOwnerView(mapping)

      return [owner.operatorText, owner.agencyText, mapping.mappedAt].filter(Boolean).join(' · ')
    },
    /**
     * 쓰기가 막힌 사유 — 채널마다 다르므로 행마다 한 줄이다.
     *
     * `key` 가 채널 코드만이 아닌 이유: 이 목록과 [noteGroups] 는 **같은 부모 아래 형제**라, 둘 다 `MYTOWN` 을
     * key 로 쓰면 Vue 가 `Duplicate keys detected` 를 경고하고 두 목록의 노드를 서로의 것으로 재사용한다.
     * key 는 「어느 채널인가」가 아니라 「무엇의 어느 채널인가」다 (compose 실측, 2026-09-15).
     */
    blockedLines() {
      return this.rows
        .filter((row) => !!row.chip.applyBlockedText)
        .map((row) => ({ key: `blocked-${row.partner}`, text: row.chip.applyBlockedText }))
    },
    /** 채널별 안내 불릿 — 문장도 순서도 `CHANNEL_RULES` 가 소유한다. `key` 는 [blockedLines] 와 같은 이유로 접두가 붙는다 */
    noteGroups() {
      return this.rows
        .map((row) => ({
          key: `notes-${row.partner}`,
          partner: row.partner,
          name: row.name,
          lines: channelNotes(row.partner, this.view),
        }))
        .filter((group) => group.lines.length > 0)
    },
    /** 「가입 플레이스」는 KT 축이다 — 그 채널이 없는 매장에 빈 블록을 그리지 않는다 */
    hasMytown() {
      return this.rows.some((row) => row.partner === PARTNER.MYTOWN)
    },
    places() {
      return this.view && Array.isArray(this.view.places) ? this.view.places : []
    },
  },

  watch: {
    storeCd: {
      handler(val) {
        this.guard.invalidate() // 이전 매장의 응답은 버린다
        this.loadFailed = false
        this.participationDenied = false // 권한은 매장별이다 — 앞 매장의 403 을 다음 매장에 적용하지 않는다
        this.applyView(null)
        if (val) this.load()
      },
      immediate: true,
    },
    reloadSeq() {
      if (this.storeCd) this.load()
    },
  },

  /**
   * 위저드는 **다른 창**에서 상태를 바꾼다(네이버 연동·해지). 그 창이 닫히거나 이 탭으로 돌아오면 여기 값은
   * 낡아 있다 — 돌아온 순간에 한 번 다시 읽는 것이 「연동했는데 미연동으로 보인다」를 막는 가장 싼 방법이다.
   */
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },

  destroyed() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },

  methods: {
    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      if (!this.storeCd || this.busy) return
      this.load()
    },

    receiptTextOf(link) {
      const parts = [link.receiptId, link.receiptDate].filter((v) => !!v)
      return parts.length > 0 ? parts.join(' · ') : '-'
    },

    /**
     * 응답 뷰 하나가 이 패널과 부모(헤더·목록 chip)의 유일한 출처다 — 실패(`null`)도 같은 길로 부모에 전한다.
     * 그러지 않으면 패널은 비었는데 chip 은 저장 전 상태를 계속 보인다.
     */
    applyView(data) {
      this.view = data || null
      this.$emit('updated', this.view)
    },

    async load() {
      const seq = this.guard.begin()
      const storeCd = this.storeCd
      this.loading = true
      try {
        const res = await this.$axios.get(`${PARTNER_LINK_API}/stores/${storeCd}`)
        if (!this.guard.isLatest(seq)) return
        this.loadFailed = false
        this.applyView(res.data)
      } catch (e) {
        // 인터셉터가 이미 말했다 — 여기서 두 번 말하지 않고, 실패 상태만 그린다
        if (!this.guard.isLatest(seq)) return
        this.loadFailed = true
        this.applyView(null)
      } finally {
        if (this.guard.isLatest(seq)) this.loading = false
      }
    },

    actionUrl(row, action) {
      return partnerActionUrl(this.storeCd, PARTNER_ACTION_PATH[row.partner], action)
    },

    /**
     * 조회·신청·해지·참여 — 성공하면 `updated` 에 더해 `changed` 를 낸다(판정이 바뀌었을 수 있어 부모가 형제 매장을 다시 읽는다).
     *
     * **실패는 값으로 돌려준다**(`{ status }`) — 인터셉터가 이미 서버 문장을 띄웠고, 그 코드로 무엇을 할지는
     * 축마다 다르다. 상태 코드만 돌려주면 응답이 없는 실패(오프라인·타임아웃)의 `null` 이 성공과 같은 값이 된다.
     *
     * **낡은 응답은 실패도 버린다**: `storeCd` 가 바뀌면 `watch` 가 권한 상태를 지우는데, 그 뒤 도착한 앞 매장의
     * 403 이 새 매장에 `participationDenied` 를 찍으면 바꿀 수 있는 매장의 버튼이 잠긴다. 낡음을 아는 것은
     * `seq` 를 쥔 이 함수뿐이라 판정을 호출부로 옮기지 않는다. 같은 이유로 **성공 뒤처리**도 여기서 부른다
     * (`onApplied(view)` — 최신 응답을 화면에 적용한 뒤에만. 낡은 응답에 안내를 띄우면 다른 매장의 답이 된다).
     *
     * @returns {?{status: ?number}} 성공·낡은 응답은 `null`
     */
    async post(url, body, config, onApplied) {
      const seq = this.guard.begin()
      this.busy = true
      try {
        const res = await this.$axios.post(url, body, config)
        if (!this.guard.isLatest(seq)) return null
        this.loadFailed = false
        this.applyView(res.data)
        this.$emit('changed', this.view)
        if (onApplied) onApplied(this.view)
        return null
      } catch (e) {
        if (!this.guard.isLatest(seq)) return null
        return { status: httpStatusOf(e) }
      } finally {
        if (this.guard.isLatest(seq)) this.busy = false
      }
    },

    /** 조회는 요청한 사람에게 답을 한 문장으로도 준다 — 문장과 조건은 `partnerCheckNotice` 가 소유한다 */
    check(row) {
      return this.post(this.actionUrl(row, 'check'), undefined, undefined, (view) => {
        const notice = partnerCheckNotice(view, row.partner)
        if (notice) this.$message.warning(this.$t(notice))
      })
    },

    /** §1.3 1번 — 보내는 값을 먼저 보여 주고 확인받는다 */
    confirmApply(row) {
      this.$confirm(applyConfirmMessage(this.view), this.$t(this.text.applyConfirmTitle), {
        confirmButtonText: this.$t(this.text.applyConfirmButton),
        cancelButtonText: this.$t(this.text.applyCancelButton),
        type: 'warning',
      })
        .then(() => this.post(this.actionUrl(row, 'apply')))
        .catch(() => {})
    },

    /**
     * 연동은 네아로 로그인을 거치므로 이 패널이 직접 하지 않는다 — 위저드를 새 창으로 연다(§2.1 C).
     * 돌아오면 `visibilitychange` 가 다시 읽는다.
     *
     * ★ **`noopener` 를 주지 않는다.** 이 앱의 인증은 `sessionStorage`(`vuex_auth_*`)에 산다. 브라우저는
     * 세션 저장소를 **보조 브라우징 컨텍스트**(opener 를 가진 새 탭)에만 복제하므로, `noopener` 로 열면
     * 새 탭은 로그아웃 상태로 떠서 위저드가 「로그인이 필요합니다」에서 멈춘다 — 어드민 진입 경로가 통째로
     * 막힌다. 그 대신 **위저드가 뜨자마자 스스로 `window.opener` 를 끊는다**(`NaverPlaceLinkView.created`):
     * 세션은 복제된 뒤이고, 네이버로 이동한 다음에는 opener 가 남아 있지 않다.
     */
    openWizard(row) {
      const opened = window.open(naverPlaceWizardPath(this.storeCd), '_blank')
      if (!opened) this.$message.warning(this.$t(this.text.wizardPopupBlocked))
    },

    confirmUnlink(row) {
      this.$confirm(unlinkConfirmMessage(this.view, row.link), this.$t(this.text.unlinkConfirmTitle), {
        confirmButtonText: this.$t(this.text.unlinkConfirmButton),
        cancelButtonText: this.$t(this.text.unlinkCancelButton),
        type: 'warning',
      })
        .then(() => this.post(this.actionUrl(row, 'unlink')))
        .catch(() => {})
    },

    /** 참여 경로 — 채널과 같은 base 아래 `callpop` 조각이다(`utils/partnerLink` 가 모양을 소유한다) */
    participationUrl(action) {
      return partnerActionUrl(this.storeCd, PARTICIPATION_ACTION_PATH, action)
    },

    /**
     * 참여 쓰기의 뒤처리 — 갈래 판정은 순수 함수가 하고 여기서는 그 낱말에 답만 한다.
     *
     * `CONFLICT`(409)에 **다시 읽는** 이유: 서버가 「이미 참여 중」이라고 말한 순간 화면의 상태는 낡은 것이다.
     * 그대로 두면 사장님이 같은 버튼을 다시 누른다.
     */
    afterParticipationWrite(failure) {
      const result = participationWriteResult(failure)
      if (result === PARTICIPATION_WRITE_RESULT.DENIED) {
        this.participationDenied = true
        return
      }
      if (result === PARTICIPATION_WRITE_RESULT.CONFLICT) this.load()
    },

    /** 등록은 본문이 없다(B1 보고서 §2) — `$confirm` 은 무엇이 바뀌는지 먼저 말하는 자리다 */
    confirmJoin() {
      this.$confirm(joinConfirmMessage(this.view), this.$t(this.text.joinConfirmTitle), {
        confirmButtonText: this.$t(this.text.joinConfirmButton),
        cancelButtonText: this.$t(this.text.applyCancelButton),
        type: 'warning',
      })
        .then(async () => {
          this.afterParticipationWrite(
            await this.post(this.participationUrl('join'), undefined, PARTICIPATION_WRITE_CONFIG)
          )
        })
        .catch(() => {})
    },

    /**
     * 해지는 **사유(선택)** 를 받는다 — `$prompt` 의 입력값이 그대로 요청 본문의 `reason` 이 된다.
     * 빈 입력은 키를 싣지 않는다([participationEndBody]). 길이는 서버 상한과 같은 값으로 입력에서 막는다.
     */
    confirmEnd() {
      this.$prompt(endConfirmMessage(this.view), this.$t(this.text.endConfirmTitle), {
        confirmButtonText: this.$t(this.text.endConfirmButton),
        cancelButtonText: this.$t(this.text.applyCancelButton),
        inputPlaceholder: this.$t(this.text.endReasonPlaceholder),
        inputValue: '',
        inputValidator: (value) => (value || '').length <= PARTICIPATION_REASON_MAX,
        inputErrorMessage: this.$t(this.text.endReasonTooLong),
        type: 'warning',
      })
        .then(async ({ value }) => {
          const body = participationEndBody(value)
          this.afterParticipationWrite(await this.post(this.participationUrl('end'), body, PARTICIPATION_WRITE_CONFIG))
        })
        .catch(() => {})
    },

    showInfo(row) {
      this.$alert(partnerInfoMessage(this.view, row.link), this.$t(partnerLongName(row.partner)), {
        confirmButtonText: this.$t('확인'),
      })
    },

    copyRequestId(row) {
      const id = row.link.lastRequestId
      if (!id) return
      return copyText(id).then((copied) =>
        copied
          ? this.$message.success(this.$t(this.text.requestIdCopied))
          : this.$message.warning(this.$t(this.text.copyFailed))
      )
    },
  },
}
</script>

<style scoped>
.partner-link-panel h4 {
  margin: 16px 0 8px;
}
.partner-link-panel .muted {
  color: #909399;
}
.partner-link-panel .request-id {
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
}
.partner-link-panel .participation {
  margin: 0 0 12px;
}
.partner-link-panel .participation__note {
  color: #909399;
  font-size: 12px;
  margin: 6px 0 0;
}
.partner-link-panel .participation__mapping {
  color: #606266;
  font-size: 12px;
  margin: 4px 0 0;
}
.partner-link-panel .participation__mapping-title {
  font-weight: 600;
  margin-right: 8px;
}
.partner-link-panel .blocked {
  color: #e6a23c;
  font-size: 12px;
  margin: 8px 0 0;
}
.partner-link-panel .notes-group {
  margin: 12px 0 0;
}
.partner-link-panel .notes-group__title {
  color: #606266;
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 4px;
}
.partner-link-panel .notes {
  color: #909399;
  font-size: 12px;
  line-height: 1.8;
  margin: 0;
  padding-left: 18px;
}
</style>
